import { HttpClient, HttpParams } from "@angular/common/http";
import { inject } from "@angular/core";
import { Observable } from "rxjs";

/**
 * @abstract
 * `ApiService` serves as a base abstract class for API interactions.
 * 
 * This class provides foundational HTTP functionalities that can be extended 
 * by other services in the application. By using an abstract class, common 
 * API-related logic and utilities are centralized, promoting reusability and 
 * consistent patterns across services.
 * 
 * Key Features:
 * - Contains methods for common HTTP operations (e.g., GET, POST).
 * - Utilizes Angular's `HttpClient` for HTTP requests.
 * - Abstract nature enforces that this class is not directly instantiated, 
 *   but extended by concrete service classes.
 * 
 * @example
 * @Injectable({
 *   providedIn: 'root',
 * })
 * export class UserService extends ApiService {
 *   getUserById(id: number): Observable<User> {
 *     return this.get<User>(`users/${id}`);
 *   }
 * }
 */
export abstract class ApiService {

  //#region Dependencies
  /**
   * Injects Angular's `HttpClient` service to handle HTTP requests.
   * 
   * The `inject()` function is used for Dependency Injection, allowing the service
   * to access Angular's HttpClient without explicitly declaring it in the constructor.
   */
  readonly #http: HttpClient = inject(HttpClient);
  //#endregion

  //#region Configuration and Constructor
  /**
   * Constructor for the ApiService.
   * 
   * Currently, this constructor does not require any parameters or logic,
   * as dependencies are injected via `inject()` and constants are defined separately.
   */
  constructor(private readonly API_URL: string) { }
  //#endregion

  //#region REST Methods
  /**
   * A helper method to perform a generic GET HTTP request.
   * 
   * @template T - Represents the type of data returned. 
   *               Ensures type safety by returning an `Observable<T>`.
   * @param endpoint - The API endpoint to send the GET request to.
   *                   Combined with `API_URL` to form the full URL.
   * @returns {Observable<T>} - An Observable representing the HTTP response.
   *                            Emits data of the specified type (`T`).
   * 
   * @example
   * this.get<User>('users/1').subscribe((user: User) => {
   *     console.log(user); // Logs User data
   * });
   */
  protected get<T>(endpoint: string, params?: HttpParams | Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>>): Observable<T> {
    return this.#http.get<T>(`${this.API_URL}/${endpoint}`, { params });
  }

  /**
   * A helper method to perform a generic POST HTTP request.
   *
   * @template T - Represents the type of data returned by the server. 
   *               Ensures type safety by returning an `Observable<T>`.
   * @param endpoint - The API endpoint to send the POST request to.
   *                   Combined with `API_URL` to form the full URL.
   * @param body - The payload to be sent with the POST request.
   *               This is typically a JSON object representing the request data.
   * @returns {Observable<T>} - An Observable representing the HTTP response.
   *                            Emits data of the specified type (`T`).
   *
   * @example
   * const payload = { name: 'John Doe', age: 30 };
   * this.post<User>('users', payload).subscribe((user: User) => {
   *   console.log(user); // Logs User data
   * });
   */
  protected post<T>(endpoint: string, body: any | null, params?: HttpParams | Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>>): Observable<T> {
    return this.#http.post<T>(`${this.API_URL}/${endpoint}`, body, { params });
  }

  /**
   * A helper method to perform a generic PUT HTTP request.
   *
   * @template T - Represents the type of data returned by the server. 
   *               Ensures type safety by returning an `Observable<T>`.
   * @param endpoint - The API endpoint to send the PUT request to.
   *                   Combined with `API_URL` to form the full URL.
   * @param body - The payload to be sent with the PUT request.
   *               This is typically a JSON object representing the request data.
   * @returns {Observable<T>} - An Observable representing the HTTP response.
   *                            Emits data of the specified type (`T`).
   *
   * @example
   * const payload = { name: 'Jane Doe', age: 25 };
   * this.put<User>('users/2', payload).subscribe((user: User) => {
   *   console.log(user); // Logs User data
   * });
   */
  protected put<T>(endpoint: string, body: any | null, params?: HttpParams | Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>>): Observable<T> {
    return this.#http.put<T>(`${this.API_URL}/${endpoint}`, body, { params });
  }

  /**
   * A helper method to perform a generic DELETE HTTP request.
   *
   * @template T - Represents the type of data returned by the server. 
   *               Ensures type safety by returning an `Observable<T>`.
   * @param endpoint - The API endpoint to send the DELETE request to.
   *                   Combined with `API_URL` to form the full URL.
   * @returns {Observable<T>} - An Observable representing the HTTP response.
   *                            Emits data of the specified type (`T`).
   *
   * @example
   * this.delete<User>('users/3').subscribe((user: User) => {
   *   console.log(user); // Logs User data
   * });
   */
  protected delete<T>(endpoint: string, params?: HttpParams | Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>>): Observable<T> {
    return this.#http.delete<T>(`${this.API_URL}/${endpoint}`, { params });
  }
  //#endregion
}
